/* eslint-disable */

import { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Login from './pages/Login/Login';
import { Layout } from './components/Layout/Layout';
import { setGlobalLoading } from './Redux/slices/globalSlice';
import { setAuthenticated } from './Redux/slices/authSlice';
import { signOut } from '@aws-amplify/auth';
import { getUser } from './api/user/post';

import { AppState } from './Redux/store';
import './globalStyles/styles.css';
import PublicRoute from './components/PublicRoute/PublicRoute';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import FullPageLoader from './components/FullPageLoader/FullPageLoader';
import { setUser } from './Redux/slices/userSlice';
import Setup from './pages/Setup/Setup';
import { updateCompanyPaymentMethod } from './api/company/post';
import { setSites } from './Redux/slices/allSites';
import { setUsers } from './Redux/slices/allUsers';
import Visitors from './pages/Visitors/Visitors';
import { setCurrentSite } from './Redux/slices/currentSiteSlice';
import { setLogEntires } from './Redux/slices/logEntires';
import Reports from './pages/Reports/Reports';
import {
  setChecklistTabItems,
  setInventoryTabItems,
  setReportsTabItems,
  setTrackerTabItems,
} from './Redux/slices/tabSlice';
import {
  setChecklistLayouts,
  setInventoryLayouts,
  setReportLayouts,
  setTrackerLayouts,
} from './Redux/slices/UIBuilderLayouts';

import RenderFormData from './components/RenderFormData/RenderFormData';
import { setReportDataFromBins } from './Redux/slices/reportDataSlice';
import UserData from './pages/UserData/UserData';
import { setShiftToDoItems } from './Redux/slices/shiftToDoSlice';
import Dashboard from './pages/Dashboard/Dashboard';
import moment from 'moment';
import { getShiftProximity } from './utils/shiftProximity';
import { setVisitorLog } from './Redux/slices/visitorLogSlice';
import { RealtimeSocketBridge } from './realtime/RealtimeSocketBridge';
import RedZone from './pages/Reports/standardReports/RedZone/RedZone';
import IncidentReport from './pages/Reports/standardReports/IncidentReport/IncidentReport';
import Deliveries from './pages/Deliveries/Deliveries';
function App() {
  const { userType } = useSelector((state: any) => state.user);

  const dispatch = useDispatch();
  const authenticated = useSelector(
    (state: AppState) => state.auth.authenticated
  );
  const globalLoading = useSelector(
    (state: AppState) => state.global.globalLoading
  );

  //connects socket.io - implemented logic to avoid double connects in strict mode.

  async function authenticateToken() {
    dispatch(setGlobalLoading(true));
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');

      if (sessionId) {
        // Send session_id to backend for verification
        const updateResponse = await updateCompanyPaymentMethod(sessionId);

        if (!(updateResponse as any).success) {
          throw new Error('Failed to update payment method.');
        }

        // Clear session_id from URL after successful update
        window.history.replaceState(null, '', window.location.pathname);
      }

      const res = await getUser();

      if (res?.success) {
        if (res?.data?.defaultPaymentSession?.url) {
          window.location.href = res.data.defaultPaymentSession.url;
        }

        //initializes the site selection
        const selectedSite = res?.data?.selectedSite ?? {
          siteId: '0000',
          siteName: 'MASTER',
          timezone: 'America/Los_Angeles',
          shiftStart: '06:00',
          siteStreet: '',
          siteCity: '',
          siteState: '',
          siteZip: '',
        };

        if (res.data.reports) {
          dispatch(
            setReportDataFromBins({
              bins: res.data.reports,
              timezone: selectedSite.timezone || 'America/Los_Angeles',
            })
          );
        }

        if (res.data.shiftToDo) {
          if (res.data.shiftToDo.length) {
            dispatch(setShiftToDoItems(res.data.shiftToDo));
          }
        }

        if (res.data.reportCategories) {
          dispatch(setTrackerTabItems(res.data.reportCategories.trackers));
          dispatch(setInventoryTabItems(res.data.reportCategories.inventories));
          dispatch(setChecklistTabItems(res.data.reportCategories.checklists));
          dispatch(setChecklistLayouts(res.data.reportCategories.checklists));
          dispatch(setTrackerLayouts(res.data.reportCategories.trackers));
          dispatch(setInventoryLayouts(res.data.reportCategories.inventories));
          dispatch(setReportLayouts(res.data.reportCategories.reports));
          dispatch(setReportsTabItems(res.data.reportCategories.reports));
        }

        dispatch(setCurrentSite(selectedSite));
        dispatch(setUser(res.data));
        dispatch(setUsers(res.data.associatedUsers));
        dispatch(setSites(res.data.associatedSites));
        dispatch(setAuthenticated(true));
        //no log entries needed for all access user
        if (res.data.associatedCompany !== 'ALL_ACCESS') {
          dispatch(setLogEntires(res.data.logEntries));
          dispatch(setVisitorLog(res?.data.visitorLog));
        }
        dispatch(setAuthenticated(true));
      } else {
        signOut();
        dispatch(setAuthenticated(false));
      }
    } catch (err) {
      signOut();
      dispatch(setAuthenticated(false));
    } finally {
      dispatch(setGlobalLoading(false));
    }
  }

  useEffect(() => {
    authenticateToken();
  }, []);

  return (
    <Router>
      {authenticated && <RealtimeSocketBridge />}
      <Routes>
        {/* Default route */}
        <Route
          path="/"
          element={
            globalLoading === undefined || globalLoading ? (
              <FullPageLoader />
            ) : authenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Login route */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        <Route
          path="/setup"
          element={
            <ProtectedRoute>
              <Layout>
                <Setup />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Dashboard route */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Visitor Log Route */}
        <Route
          path="/visitor_log"
          element={
            <ProtectedRoute>
              <Layout>
                <Visitors />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Red Zone Analysis Route */}
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <Layout>
                <Reports type="REPORT" />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/incident_report"
          element={
            <ProtectedRoute>
              <Layout displayOff={true}>
                <IncidentReport />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/deliveries"
          element={
            <ProtectedRoute>
              <Layout displayOff={true}>
                <Deliveries />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Red Zone Analysis Route */}
        <Route
          path="/reports/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <RenderFormData stateAccessor="reportLayouts" />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/userInfo/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <UserData />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/red_zone_report"
          element={
            <ProtectedRoute>
              <Layout displayOff={true}>
                <RedZone />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/inventories"
          element={
            <ProtectedRoute>
              <Layout>
                <Reports type="INVENTORIES" />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventories/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <RenderFormData stateAccessor="inventoryLayouts" />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/checklists"
          element={
            <ProtectedRoute>
              <Layout>
                <Reports type="CHECKLISTS" />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/checklists/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <RenderFormData stateAccessor="checklistLayouts" />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/custody_trackers"
          element={
            <ProtectedRoute>
              <Layout>
                <Reports type="TRACKER" />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/trackers/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <RenderFormData stateAccessor="trackerLayouts" />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Lockup Report Route */}
        {/* <Route
          path="/lockup_report"
          element={
            <ProtectedRoute>
              <Layout>
                <h1>Lockup Report</h1>
              </Layout>
            </ProtectedRoute>
          }
        /> */}

        {/* Threat assessment */}
        {/* <Route
          path="/threat_assessment"
          element={
            <ProtectedRoute>
              <Layout>
                <>
                  <p>Threat Assessment</p>
                  <p>Pursuer Database</p>
                  <p>Pursuer handwriting AI to check letters</p>
                </>
              </Layout>
            </ProtectedRoute>
          }
        /> */}
        {/* Lockup Report Route */}
        {/* <Route
          path="/incident_report"
          element={
            <ProtectedRoute>
              <Layout>
                <h1>Incident Report</h1>
              </Layout>
            </ProtectedRoute>
          }
        /> */}
        {/* Lockup Report Route */}
        {/* <Route
          path="/systems_check"
          element={
            <ProtectedRoute>
              <Layout>
                <h1>Systems Check</h1>
              </Layout>
            </ProtectedRoute>
          }
        /> */}
        {/* Lockup Report Route */}
        {/* <Route
          path="/disaster_notification"
          element={
            <ProtectedRoute>
              <Layout>
                <h1>Disaster Notification</h1>
              </Layout>
            </ProtectedRoute>
          }
        /> */}
        {/* Lockup Report Route */}
        {/* <Route
          path="/protectee_research"
          element={
            <ProtectedRoute>
              <Layout>
                <h1>Protectee Research</h1>
              </Layout>
            </ProtectedRoute>
          }
        /> */}

        {/* Lockup Report Route */}
        {/* <Route
          path="/background_check"
          element={
            <ProtectedRoute>
              <Layout>
                <h1>Background Check</h1>
              </Layout>
            </ProtectedRoute>
          }
        /> */}
      </Routes>
    </Router>
  );
}

export default App;
